import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import SimpleBar from 'simplebar';

import SelectOption from '../SelectOption/SelectOption';

import 'simplebar/dist/simplebar.css';
import styles from './dropdown.scss';

/**
 * Dropdown menu. Can be applied for any other component.
 */
export default function Dropdown(props) {
    const isControlledOutside = props.shown !== undefined;
    const [shown, setShown] = useState(props.shown || false);
    const rootEl = useRef(null);
    const popoutsRoot = document.getElementById('popouts-portal');

    const onRefChange = useCallback(node => {
        if (node !== null) {
            const simplebar = new SimpleBar(node);
            const scrollElement = simplebar.getScrollElement();
            const simplebarContent = simplebar.getContentElement().children;
            const root = rootEl.current;

            if (root !== null) {
                const rootRect = root.getBoundingClientRect();
                const panelContent = root.closest('.panel-in');

                const panelScrollTop = panelContent.scrollTop;

                // Removed panelContent.offsetTop.
                const panelOffsetTop = panelContent.getBoundingClientRect().y;
                const nodeRect = node.getBoundingClientRect();
                // X Position
                node.style.width = rootRect.width + 'px';
                const popoutsPortalOffsetLeft = getPopoutsPortal().getBoundingClientRect().left;
                // If we don't have enough space for the dropdown.
                const dx = window.innerWidth - (rootRect.left + nodeRect.width);

                if (dx < 0) {
                    node.style.left = rootRect.left - popoutsPortalOffsetLeft - nodeRect.width + rootRect.width + 'px';
                } else {
                    node.style.left = rootRect.left - popoutsPortalOffsetLeft + 'px';
                }
                // Y Position
                const gapToNode = 4;
                const needSpace = rootRect.top + rootRect.height + gapToNode + nodeRect.height;
                const screenBottom = window.innerHeight;

                if (needSpace > screenBottom) {
                    node.style.top = rootRect.top + panelScrollTop - nodeRect.height - gapToNode - panelOffsetTop + 'px';
                } else {
                    node.style.top = rootRect.top + panelScrollTop + rootRect.height + gapToNode - panelOffsetTop + 'px';
                }

                // center on the selected element
                if (simplebarContent && simplebarContent.length > 1) {
                    for (let i = 0; i < simplebarContent.length; i++) {
                        const item = simplebarContent[i];

                        if (item.getAttribute('data-selected') === 'true') {
                            const itemTop = item.offsetTop;
                            const itemHeight = item.offsetHeight;
                            scrollElement.scrollTop = itemTop - nodeRect.height / 2 + itemHeight;
                        }
                    }
                }
            }
        }
    }, []);

    /**
     * Opens dropdown menu if shown prop is undefined.
     * @param {object} e Triggered click event.
     */
    function handleRootClick(e) {
        if (isControlledOutside) return;
        setShown(!shown);
        e.stopPropagation();
    }

    /**
     * Handles click by option.
     * @param {object} option Option clicked on.
     */
    function handleOptionClick(option) {
        if (typeof option.onClick === 'function') {
            option.onClick();
        } else {
            console.warn('Please, provide OnClick property for option: ', option.value);
        }
        setShown(false);
    }

    /**
     * Handling outside of dropdown menu MouseDown event.
     * Close menu if clicked outside.
     * @param {event} e triggered event
     */
    function handleOutsideDown(e) {
        const popouts = getPopoutsPortal();

        if (popouts) {
            const isMenuClicked = popouts.contains(e.target) || rootEl.current.contains(e.target);

            if (shown && !isMenuClicked) {
                setShown(false);
            }
        }
    }

    /**
     * Renders dropdown menu options.
     * @returns {array} Array of objects to render.
     */
    function renderOptions() {
        if (typeof props.renderOptions === 'function') {
            // call custom render options function provided by user.
            return props.renderOptions(props.options);
        }

        // default renderer
        return props.options !== undefined && props.options.map((item) => {
            return <SelectOption
                key={item.value}
                onMouseDown={(e) => { e.preventDefault(); }}
                onClick={(e) => { handleOptionClick(item); }}
                label={item.label}
                icon={item.icon}
                disabled={item.disabled}
            />;
        });
    }

    /**
     * Decect shown state variable change and trigger OnOpen / OnClose events.
     */
    useEffect(() => {
        if (isControlledOutside) return;

        if (shown === true) {
            window.addEventListener('mousedown', handleOutsideDown);

            if (typeof props.onOpen === 'function') {
                props.onOpen();
            }
        }

        return () => {
            window.removeEventListener('mousedown', handleOutsideDown);
        };
    }, [shown, handleOutsideDown]);

    /**
     * Component DidMount / DidUpdate.
     */
    useEffect(() => {
        if (typeof props.getRootRef === 'function') {
            props.getRootRef(rootEl);
        }
    });

    function getPopoutsPortal() {
        const panelContent = rootEl.current.closest('.panel-in');

        if (panelContent) {
            const portals = panelContent.getElementsByClassName('popouts-portal');

            if (portals && portals.length > 0) {
                return portals[0];
            }
        }

        return popoutsRoot;
    }

    return (
        <div className={styles.wrapper} ref={rootEl}>
            {React.cloneElement(props.children, { onClick: handleRootClick })}
            {(props.shown || shown) &&
                ReactDOM.createPortal(
                    <div className={styles.menu} ref={onRefChange}>
                        {renderOptions()}
                    </div>,
                    getPopoutsPortal()
                )
            }
        </div>
    );
};

Dropdown.propTypes = {
    children: PropTypes.any,
    /**
     * Is Dropdown menu visible.
     */
    shown: PropTypes.bool,
    /**
     * Function calls on dropdown menu opened.
     */
    onOpen: PropTypes.func,
    /**
     * Array of objects with value and label fields.
     */
    options: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })),
    /**
     * Returns ref to root DOM element.
     */
    getRootRef: PropTypes.func,

    /**
     * Custom render optinons method. Useful when you need to display custom content in the dropdown.
     */
    renderOptions: PropTypes.func,
};
